import React, { memo, ReactNode, useEffect } from 'react'
import { Navigate, useParams } from 'react-router'

import {
  getAuthInactivityRoute,
  getDeviceOnlyRoute,
  getStandaloneLoginRoute,
  redirectToExternalUrl,
  useLocationToVisitStorage,
} from '@surglogs-app/routing'

import { decodeOnlyDeviceClinicIdsFromJwtToken } from '../helpers'
import { useAuthToken, useClinicContext } from '../hooks'

import { useStorageCheck } from './hooks/useStorageCheck'
import { TIMER_REFRESH_TIMESTAMP } from './PrivateRouteSession'
import { PrivateRouteSessionWrapper } from './PrivateRouteSessionWrapper'

interface PrivateRouteProps {
  children: ReactNode
  accessManagementApiVersion: string
  referrer?: string
  organization?: string
}

const LoginRedirect = ({
  referrer,
  organization,
}: Pick<PrivateRouteProps, 'referrer' | 'organization'>) => {
  const isProcessingLogout = sessionStorage.getItem(TIMER_REFRESH_TIMESTAMP)

  useEffect(() => {
    if (!isProcessingLogout) {
      redirectToExternalUrl(getStandaloneLoginRoute({ referrer, organization }))
    }
  }, [isProcessingLogout, organization, referrer])

  if (isProcessingLogout) {
    return <Navigate {...{ to: getAuthInactivityRoute() }} />
  }

  return null
}

export const PrivateRoute = memo(
  ({
    accessManagementApiVersion,
    children,
    referrer,
    organization,
  }: PrivateRouteProps) => {
    const { token } = useAuthToken()
    const params = useParams<{ clinicId: string }>()
    const { setValue: setLocationToVisitAfterLogin } =
      useLocationToVisitStorage()

    if (!token && window.location.pathname !== getAuthInactivityRoute()) {
      setLocationToVisitAfterLogin(window.location)
    }

    useStorageCheck(Number(params?.clinicId))

    return token ? (
      <>
        <PrivateRouteSessionWrapper
          {...{
            apiVersion: accessManagementApiVersion,
            referrer,
            organization,
          }}
        />
        {children}
      </>
    ) : (
      <LoginRedirect {...{ referrer, organization }} />
    )
  },
)

export const PrivateRouteWithClinicCheck = memo((props: PrivateRouteProps) => {
  const { token } = useAuthToken()
  const { clinicId } = useClinicContext()
  const params = useParams<{ clinicId: string }>()

  useStorageCheck(Number(params?.clinicId))

  if (
    token &&
    clinicId &&
    decodeOnlyDeviceClinicIdsFromJwtToken(token).includes(Number(clinicId))
  ) {
    return <Navigate {...{ to: getDeviceOnlyRoute() }} />
  }

  return <PrivateRoute {...props} />
})
